"use client";
import React, { useEffect, useState } from "react";
import Filters from "@/components/icons/Filters";
import CTALink from "@/components/CTA/ctaLink";
import ProductCardList from "@/components/ProductCardList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ElementType, FormatSubType } from "@/constants/tagular/main";
import { ProductType as CourseType } from "@/common/course/types";
import { ProductSummary } from "@/common/productSummary/types";
import getIconFromName from "@/utils/functions/getIconfromName";
import { ProgramType } from "@/common/program/types";
import { Category, ProductCardTabsByTopicProps } from "./types";
import { personalizeProductList } from "@/lib/productSummary/helper/personalizeProductList";
import { sanitizeTabValue } from "@/utils/sanitizeTabValue";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { stringToAlgoliaQuery } from "@/lib/algolia/stringToAlgoliaQuery";
import { normalizeSubject } from "./normalizeSubject";
import {
  getCategoryTitleKey,
  resolveHeading,
  resolveTabContent,
} from "./resolveTabContent";
import useTranslations from "@/i18n/useTranslations";
import type { Messages } from "@/i18n/useTranslations";

type ProductCardListMessageKey = keyof Messages["ProductCardList"];

// Helper function to create unique tab values with location
const createTabValue = (label: string, location?: string): string => {
  const sanitizedLabel = sanitizeTabValue(label);
  return location
    ? `${sanitizedLabel}-${sanitizeTabValue(location)}`
    : sanitizedLabel;
};

type CardListProps = {
  products: ProductSummary[];
  isMobile: boolean;
  location: string;
  cardFormatSubtype?: FormatSubType;
};

const CardList = ({
  products,
  isMobile,
  location,
  cardFormatSubtype,
}: CardListProps) => (
  <div className={`${isMobile ? "lg:hidden" : "hidden lg:block"}`}>
    <ProductCardList
      products={products}
      shouldScroll={isMobile}
      location={location}
      cardFormatSubType={cardFormatSubtype}
    />
  </div>
);

const productTypeString = (productType: string): string => {
  const indexName = PRODUCT_INDEX_EN;
  const learningTypeFacet = getFacetAttributeName("learning_type", {
    indexName,
  });

  if (
    productType === ProgramType.MASTERS ||
    productType === ProgramType.BACHELORS ||
    productType === ProgramType.DOCTORATE
  ) {
    return `tab=degree-program&${learningTypeFacet}=${productType}`;
  }
  if (productType === ProgramType.CERTIFICATE) {
    return `tab=program`;
  }

  const formattedProductType = productType.replace(/\s+/g, "-");
  return `tab=${formattedProductType}&${learningTypeFacet}=${productType}`;
};

const ProductCardTabsByTopicClient = ({
  title,
  topic,
  subject,
  categories,
  location = "",
  cardFormatSubtype = FormatSubType.Grid,
  showNumCertificates = 16,
  showNumCourses = 16,
  showNumEe = 16,
  showNumMasters = 16,
  showNumBachelors = 16,
  showNumDoctorate = 16,
  heading,
  certSubtitle,
  coursesSubtitle,
  eeSubtitle,
  mastersSubtitle,
  bachelorsSubtitle,
  doctorateSubtitle,
  certData,
  coursesData,
  eeData,
  mastersData,
  bachelorsData,
  doctorateData,
}: ProductCardTabsByTopicProps) => {
  const tCommon = useTranslations("Common");
  const t = useTranslations("ProductCardList");
  const translateList = (key: string) => t(key as ProductCardListMessageKey);
  const localizeCategoryLabel = (productType: string, fallback: string) => {
    const titleKey = getCategoryTitleKey(productType);
    return titleKey ? translateList(titleKey) : fallback;
  };
  const overrides = {
    certSubtitle,
    coursesSubtitle,
    eeSubtitle,
    mastersSubtitle,
    bachelorsSubtitle,
    doctorateSubtitle,
    certData,
    coursesData,
    eeData,
    mastersData,
    bachelorsData,
    doctorateData,
  };
  const canonicalSubject = normalizeSubject(subject, topic);
  const [filteredCategories, setFilteredCategories] =
    useState<Category[]>(categories);
  const [activeCategory, setActiveCategory] = useState<Category | undefined>(
    () => {
      return categories.length > 0 ? categories[0] : undefined;
    },
  );

  useEffect(() => {
    // Personalize and remove any items exluded in the shortcode
    const newCategories =
      categories?.filter(
        (category) => category.productType !== CourseType.BOOTCAMP,
      ) || [];
    setFilteredCategories(newCategories);
  }, [categories]);

  const personalizedCategories = filteredCategories
    .map((category) => {
      const personalizedProducts = personalizeProductList(
        category.products ?? [],
      );
      return {
        ...category,
        categoryLabel: localizeCategoryLabel(
          category.productType,
          category.categoryLabel,
        ),
        products: personalizedProducts,
      };
    })
    .filter((category) => category.products && category.products.length > 0);

  useEffect(() => {
    if (
      !personalizedCategories.find(
        (c) => c.productType === activeCategory?.productType,
      )
    ) {
      setActiveCategory(personalizedCategories[0]);
    }
  }, [personalizedCategories, activeCategory]);

  if (personalizedCategories.length === 0) {
    return null;
  }

  // `activeCategory` is initialized from server categories, whose
  // `categoryLabel` is still an i18n key (e.g. "certificateTitle"). Always
  // read the localized copy from `personalizedCategories` so the heading
  // doesn't render that key on first paint / SSR.
  const resolvedActiveCategory =
    personalizedCategories.find(
      (category) => category.productType === activeCategory?.productType,
    ) ?? personalizedCategories[0];

  const handleChange = (sanitizedValue: string) => {
    const selectedCategory = personalizedCategories.find(
      (category) =>
        createTabValue(category.productType, location) === sanitizedValue,
    );
    setActiveCategory(selectedCategory);
  };

  const activeTabContent = resolveTabContent(
    resolvedActiveCategory.productType,
    overrides,
    translateList,
  );
  // Tabs stay title-cased ("Certificates"); the heading is sentence case
  // ("Browse online Artificial Intelligence certificates").
  const headingCategory = resolvedActiveCategory.categoryLabel.toLowerCase();
  const activeHeading =
    heading && heading.trim().length > 0
      ? resolveHeading(heading, title, headingCategory)
      : t("browseOnlineHeading", {
          title: title ?? "",
          category: headingCategory,
        })
          .replace(/\s+/g, " ")
          .trim();

  return (
    <div className="container py-3">
      <Tabs
        value={createTabValue(resolvedActiveCategory.productType, location)}
        onValueChange={handleChange}
      >
        {activeTabContent && (
          <div>
            <h2 className="text-xl lg:text-3xl font-bold">{activeHeading}</h2>
            <h4 className="elm-h5 font-normal my-4">
              {activeTabContent.subtitle}
            </h4>
            <div className="lg:grid lg:grid-cols-3 lg:gap-4 border-t-solid border-t-[1px] border-b-solid border-b-[1px] py-6 mb-6">
              {activeTabContent.data.map((item, index) => (
                <div key={index} className="flex">
                  <div className="w-12 text-primary">
                    {getIconFromName(item.icon)}
                  </div>
                  <div className="px-3 text-primary">
                    <h3 className="mt-0 text-base text-primary">
                      {item.title}
                    </h3>
                    <p className="text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="hidden lg:flex justify-center">
          <TabsList className="shadow-lg rounded-t-xl max-w-[1128px]">
            {personalizedCategories.map((category) => {
              const sanitizedValue = createTabValue(
                category.productType,
                location,
              );

              return (
                <TabsTrigger
                  key={category.productType}
                  value={sanitizedValue}
                  className="no-underline font-normal text-[16px] text-primary hover:text-primary data-[state=active]:font-bold bg-white rounded-none first:rounded-tl-xl last:rounded-tr-xl"
                >
                  {category.categoryLabel} {`(${category.products?.length})`}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
        <div className="lg:hidden">
          <Select
            value={createTabValue(resolvedActiveCategory.productType, location)}
            onValueChange={handleChange}
          >
            <SelectTrigger className="flex items-center border-solid border-[1px] border-primary shadow-none text-primary no-underline">
              <Filters />
              <SelectValue placeholder={tCommon("selectAProductType")} />
            </SelectTrigger>
            <SelectContent>
              {personalizedCategories.map((category) => (
                <SelectItem
                  key={category.productType}
                  value={createTabValue(category.productType, location)}
                >
                  {category.categoryLabel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {personalizedCategories.map((category) => {
          const productType = productTypeString(category.productType);
          // CTA URL strategy:
          //   • Keyword-fallback tab → `?q=<topic>` only. Subject didn't
          //     match anything for this tab, so adding it would over-filter
          //     the destination search page.
          //   • Subject-sourced tab → `?q=<topic>&subject=<canonical>`.
          //     Carries the broader subject categorization AND the page's
          //     specific search intent (e.g. niche topic "graphic design"
          //     under broader subject "Design").
          //   • Subject set but no topic (rare) → `?subject=<canonical>`.
          const queryParam = topic ? `q=${stringToAlgoliaQuery(topic)}` : "";
          const subjectParam =
            canonicalSubject.length > 0
              ? `subject=${encodeURIComponent(canonicalSubject)}`
              : "";

          let ctaUrl: string;
          if (category.searchMode === "keyword") {
            ctaUrl = queryParam
              ? `/search?${productType}&${queryParam}`
              : `/search?${productType}`;
          } else {
            const params = [queryParam, subjectParam].filter(Boolean).join("&");
            ctaUrl = params
              ? `/search?${productType}&${params}`
              : `/search?${productType}`;
          }

          const showCountByType: Record<string, number> = {
            [ProgramType.CERTIFICATE]: showNumCertificates,
            [CourseType.COURSE]: showNumCourses,
            [CourseType.EXECUTIVE_EDUCATION]: showNumEe,
            [ProgramType.MASTERS]: showNumMasters,
            [ProgramType.BACHELORS]: showNumBachelors,
            [ProgramType.DOCTORATE]: showNumDoctorate,
          };

          const countOfProgramsToShow =
            showCountByType[category.productType] ?? 0;

          const sanitizedValue = createTabValue(category.productType, location);

          return (
            <TabsContent
              key={category.productType}
              value={sanitizedValue}
              className="flex flex-col max-w-[1128px] mx-auto"
            >
              <h3 className="mb-4">{category.categoryLabel}</h3>
              <CardList
                products={
                  category.products?.slice(0, countOfProgramsToShow) || []
                }
                isMobile={false}
                location={location}
                cardFormatSubtype={cardFormatSubtype}
              />
              <CardList
                products={
                  category.products?.slice(0, countOfProgramsToShow) || []
                }
                isMobile={true}
                location={location}
                cardFormatSubtype={cardFormatSubtype}
              />
              <div className="flex justify-center mx-auto mt-5">
                <CTALink
                  location={location}
                  label={t("exploreAll", { category: category.categoryLabel })}
                  url={ctaUrl}
                  html_id={`explore-all-${category.productType.replaceAll(" ", "-").toLowerCase()}-button`}
                  name={topic}
                  variant={category.productType}
                  elementType={ElementType.Entry}
                  size="lg"
                />
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default ProductCardTabsByTopicClient;
